import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { InterPoint, Logger } from '@saviynt/common';
import { Icon, SidePanel, Typography } from '@saviynt/design-system';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import { httpService } from '../../../http-service/httpService';
import {
  setConnectedTileId,
  setIsSidePanelVisible,
  setSidePanelMetaData,
} from '../../../redux/certlist/action';
import { URL_SIDE_PANEL_PROGRESS_API } from '../../../redux/certlist/urls';
import {
  SCREEN_SIZE_MOBILE_XLARGE,
  SCREEN_SIZE_TABLET_LARGE,
  SCREEN_SIZE_TABLET_SMALL,
  SCREEN_WIDTH_QUERIES,
} from '../../../utils/constant';
import Carousel from '../../Common/Carousel';
import CertsListAction from '../CertsListAction';
import {
  CAMP_TYPE_AO,
  CAMP_TYPE_EO,
  CAMP_TYPE_SA,
  CAMP_TYPE_UM,
} from '../CertsListTile/constant';
import { ACTION_BUTTON_FOOTER_KIND, CERTS_LIST_EXPIRED_TAB } from '../constant';

import CertsListSidePanelBody from './CertsListSidePanelBody';

import './CertsListSidePanel.css';

function CertsListSidePanel({ dataTestId }) {
  const dispatch = useDispatch();
  const {
    global: { mediaQueryValue },
    core: { isAdminLogin },
    sidePanel: {
      isSidePanelVisible,
      connectedTileId,
      sidePanelMetaData,
      siblingTileId,
      tempActionState, // TODO: This is the temp state till the time Carousel is updated for BreadCrumb
    },
  } = useSelector((state) => state?.certifications?.list);
  const { isMenuPinned } = useSelector((state) => state?.user);

  const certsListSidePanelRef = useRef();
  const sidePanelContainerRef = useRef(null);
  const [headerProps, setHeaderProps] = useState({
    title: '',
    description: '',
    icon: null,
  });

  const tabletLargeQuery = SCREEN_WIDTH_QUERIES.find(
    (item) => item.label === SCREEN_SIZE_TABLET_LARGE
  );
  const tabletSmallQuery = SCREEN_WIDTH_QUERIES.find(
    (item) => item.label === SCREEN_SIZE_TABLET_SMALL
  );
  const mobileXlargeQuery = SCREEN_WIDTH_QUERIES.find(
    (item) => item.label === SCREEN_SIZE_MOBILE_XLARGE
  );
  const isTabletLargeWidth = tabletLargeQuery.value <= mediaQueryValue;
  const isTabletSmallWidth = tabletSmallQuery.value <= mediaQueryValue;
  const isMobileXlargeWidth = mobileXlargeQuery.value <= mediaQueryValue;

  const sidePanelClasses = classnames(
    'CertsListSidePanel',
    isMenuPinned && 'CertsListSidePanel--isMenuPinned'
  );

  // Auto-focus the first focusable element in the side panel when it opens (desktop)
  useEffect(() => {
    if (
      isSidePanelVisible &&
      !isMobileXlargeWidth &&
      sidePanelContainerRef.current
    ) {
      // Small delay to allow the panel to render before querying focusable elements
      const timerId = setTimeout(() => {
        const focusableElement = sidePanelContainerRef.current?.querySelector(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );

        if (focusableElement) {
          focusableElement.focus();
        }
      }, 100);

      return () => clearTimeout(timerId);
    }

    return undefined;
  }, [isSidePanelVisible, connectedTileId, isMobileXlargeWidth]);

  /**
   * Manages tab order so that:
   * - Shift+Tab on the side panel's first focusable element → selected row control
   * - Tab on the side panel's last focusable element → row control after selected
   */
  useEffect(() => {
    const panelEl = sidePanelContainerRef.current;

    if (!panelEl || !isSidePanelVisible || isMobileXlargeWidth)
      return undefined;

    const FOCUSABLE_SELECTOR =
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

    const handleTabKeyDown = (e) => {
      if (e.key !== 'Tab') return;

      const focusableElements = Array.from(
        panelEl.querySelectorAll(FOCUSABLE_SELECTOR)
      );

      if (focusableElements.length === 0) return;

      const firstFocusable = focusableElements[0];
      const lastFocusable = focusableElements[focusableElements.length - 1];

      // Get all row control boxes in the list
      const allRowControls = Array.from(
        document.querySelectorAll('.CertsListTile-controlBox--isIconOnly')
      );

      // Find the index of the currently connected (selected) tile's control
      const connectedTileEl = allRowControls.find((el) => {
        const tileParent = el.closest('.CertsListTile--isConnected');

        return Boolean(tileParent);
      });
      const connectedIndex = connectedTileEl
        ? allRowControls.indexOf(connectedTileEl)
        : -1;

      if (e.shiftKey && document.activeElement === firstFocusable) {
        // Shift+Tab on first element → focus the selected row control
        e.preventDefault();

        if (connectedTileEl) {
          connectedTileEl.focus();
        }
      } else if (!e.shiftKey && document.activeElement === lastFocusable) {
        // Tab on last element → focus the row control after the selected one
        e.preventDefault();
        const nextRowControl = allRowControls[connectedIndex + 1];

        if (nextRowControl) {
          nextRowControl.focus();
        } else if (allRowControls.length > 0) {
          // If no next row, wrap or stay (focus first row as fallback)
          allRowControls[0]?.focus();
        }
      }
    };

    panelEl.addEventListener('keydown', handleTabKeyDown);

    return () => {
      panelEl.removeEventListener('keydown', handleTabKeyDown);
    };
  }, [isSidePanelVisible, connectedTileId, isMobileXlargeWidth]);

  const setSidePanelDetails = async () => {
    let latestProgressPercentage = 0;
    const { id } = sidePanelMetaData;

    if (id) {
      const decisionBody = {
        certkey: String(id),
        group_by: 'decision',
      };

      // add this implementation only when login user is not an admin and EO campaign (CR-19418) , Need optimization
      if (
        (!isAdminLogin &&
          (sidePanelMetaData?.campaignType === CAMP_TYPE_UM ||
            sidePanelMetaData?.campaignType === CAMP_TYPE_SA ||
            sidePanelMetaData?.campaignType === CAMP_TYPE_AO)) ||
        sidePanelMetaData?.campaignType === CAMP_TYPE_EO
      ) {
        try {
          const response = await httpService.post(
            URL_SIDE_PANEL_PROGRESS_API,
            decisionBody
          );

          if (response?.data) {
            const { Summary } = response.data;

            latestProgressPercentage = `${Summary.Progress}%`;
          }
        } catch (e) {
          Logger.error('setSidePanelDetails Error', e);
        }
      }

      const description = (
        <>
          <Typography
            kind='body3'
            desktop='body3-d'
            mobile='body3-d'
            color='--color-foreground-secondary-bold'>
            {sidePanelMetaData.campaignType}
          </Typography>
          <InterPoint />
          <Icon
            kind={sidePanelMetaData.statusIconKind}
            size='xSmall'
            color={sidePanelMetaData.statusIconColor}
          />
          <Typography
            kind='body3'
            desktop='body3-d'
            mobile='body3-d'
            color={sidePanelMetaData.statusTextColor}>
            {sidePanelMetaData.statusText}
          </Typography>
          {sidePanelMetaData?.tab === CERTS_LIST_EXPIRED_TAB ? (
            <div
              className='CertsListSidePanel-headerExpiredTooltip'
              data-message='Some access was automatically revoked after the certification due date.'>
              <Icon
                kind='AlertInfoFilledSeparateColors'
                color='--color-foreground-info-medium'
                size='xSmall'
              />
            </div>
          ) : (
            <Typography
              kind='body3-bold'
              desktop='body3-bold-d'
              mobile='body3-bold-d'
              color='--color-foreground-neutral-bold'>
              {latestProgressPercentage || sidePanelMetaData.progress}
            </Typography>
          )}
        </>
      );

      setHeaderProps((prev) => ({
        ...prev,
        title: sidePanelMetaData.title,
        description,
      }));
    }
  };

  useEffect(() => {
    if (sidePanelMetaData) {
      setSidePanelDetails();
    }
  }, [sidePanelMetaData]);

  const handleCloseSidePanel = () => {
    dispatch(setIsSidePanelVisible(false));
  };

  const handleNext = () => {
    if (connectedTileId !== siblingTileId?.next?.id) {
      dispatch(setConnectedTileId(siblingTileId?.next?.id));
      dispatch(setSidePanelMetaData(siblingTileId?.next));
    }
  };

  const handlePrev = () => {
    if (connectedTileId !== siblingTileId?.prev?.id) {
      dispatch(setConnectedTileId(siblingTileId?.prev?.id));
      dispatch(setSidePanelMetaData(siblingTileId?.prev));
    }
  };

  const getFooterActionBar = () => (
    <CertsListAction
      kind={ACTION_BUTTON_FOOTER_KIND.footer}
      certKey={sidePanelMetaData?.id}
      actionsMap={sidePanelMetaData?.actions}
      isPrimaryCertifierActive={sidePanelMetaData?.isPrimaryCertifierActive}
      tileCampaignKey={sidePanelMetaData?.tileCampaignKey}
      campaignType={sidePanelMetaData?.campaignType}
      tileStatusLabel={sidePanelMetaData?.tileStatusLabel}
      newCertUX={sidePanelMetaData?.newCertUX}
      certDetailsUrl={sidePanelMetaData?.certDetailsUrl}
      certificationName={sidePanelMetaData?.title}
      tileData={sidePanelMetaData}
    />
  );

  if (isMobileXlargeWidth) {
    return ReactDOM.createPortal(
      <Carousel
        isOpen={isSidePanelVisible}
        onClose={handleCloseSidePanel}
        header={{
          title: headerProps.title,
          description: headerProps.description,
        }}
        layout={
          <CertsListSidePanelBody
            handleCloseSidePanel={handleCloseSidePanel}
            tabGroupRef={certsListSidePanelRef}
          />
        }
        footer={getFooterActionBar()}
        className={sidePanelClasses}
        headerClassName='CertsListSidePanel-header'
        layoutClassName='CertsListSidePanel-layout'
        isPrevDisabled={Boolean(!siblingTileId?.prev)}
        isNextDisabled={Boolean(!siblingTileId?.next)}
        onClickNext={handleNext}
        onClickPrev={handlePrev}
        isInBackground={tempActionState}
        dataTestId={dataTestId}
      />,
      document.body
    );
  }

  return (
    <SidePanel
      ref={sidePanelContainerRef}
      isOpen={isSidePanelVisible}
      onClose={handleCloseSidePanel}
      className={sidePanelClasses}
      headerClassName='CertsListSidePanel-header'
      layoutClassName='CertsListSidePanel-layout'
      header={{
        title: headerProps.title,
        description: headerProps.description,
      }}
      layout={
        <CertsListSidePanelBody
          handleCloseSidePanel={handleCloseSidePanel}
          tabGroupRef={certsListSidePanelRef}
        />
      }
      footer={getFooterActionBar()}
      isSidePanelNavigatingList
      handlePrevious={handlePrev}
      handleNext={handleNext}
      isPreviousDisabled={Boolean(!siblingTileId?.prev)}
      isNextDisabled={Boolean(!siblingTileId?.next)}
      isTabletWidth={(isMenuPinned && isTabletLargeWidth) || isTabletSmallWidth}
      isMobileWidth={isMobileXlargeWidth}
      dataTestId={dataTestId}
    />
  );
}

CertsListSidePanel.propTypes = {
  dataTestId: PropTypes.string,
};

CertsListSidePanel.defaultProps = {
  dataTestId: 'CertListSidePanel-dataTestId',
};

export default CertsListSidePanel;
